"use client";

/**
 * WithSwipeSelector
 *
 * Mobile-first swipe selector with desktop checkbox fallback.
 * Convention: Right swipe = selection toggle, Left swipe = primary action
 *
 * @example
 * // Selection + Action (most common)
 * <WithSwipeSelector
 *   isMobile={isMobile}
 *   selection={{
 *     isSelected: isSelected,
 *     onToggle: handleToggle,
 *     humanReadable: "Select thought for batch operations"
 *   }}
 *   action={{
 *     onSwipe: handleInspect,
 *     icon: "insight",
 *     humanReadable: "View thought insights"
 *   }}
 * >
 *   <Text>Content</Text>
 * </WithSwipeSelector>
 *
 * @example
 * // Selection only
 * <WithSwipeSelector
 *   isMobile={isMobile}
 *   selection={{
 *     isSelected: isSelected,
 *     onToggle: handleToggle,
 *     humanReadable: "Select criterion"
 *   }}
 * >
 *   <Text>Content</Text>
 * </WithSwipeSelector>
 *
 * @example
 * // Custom colors and checkbox visibility
 * <WithSwipeSelector
 *   isMobile={isMobile}
 *   selection={{
 *     isSelected: isSelected,
 *     onToggle: handleToggle,
 *     showCheckboxOnDesktop: selectedCount > 0,
 *     color: "blue.500",
 *     humanReadable: "Select message"
 *   }}
 *   action={{
 *     onSwipe: handleAction,
 *     icon: "add",
 *     color: "green.500",
 *     humanReadable: "Add as origin"
 *   }}
 * >
 *   <Text>Content</Text>
 * </WithSwipeSelector>
 */

import {
  memo,
  useMemo,
  useCallback,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { Box, Checkbox, HStack, useToken } from "@chakra-ui/react";
import {
  motion,
  useAnimation,
  useMotionValue,
  useTransform,
  type PanInfo,
} from "framer-motion";
import GenericIcon, { type GenericIconType } from "../actions/GenericIcon";
import { iconSizePresets } from "@/shared/config/iconSizePresets";

const MotionBox = motion.create(Box);

// 🔧 SUPER-FAST UX CONFIG
const SWIPE_TRIGGER_DISTANCE = 60; // required distance for activation
const SWIPE_MAX_DISTANCE = 70; // max visual swipe distance
const SWIPE_REVEAL_START_DISTANCE = 28; // keep underlay hidden during tiny drags

// Ultra-fast snap-back
const RETURN_STIFFNESS = 900;
const RETURN_DAMPING = 40;

// Color
const SELECTED_BG_COLOR = "moveon.selection.bg";

// ---------------------------------
// Nested config types
// ---------------------------------
type SelectionConfig = {
  isSelected: boolean;
  checkboxIsSelected?: boolean;
  onToggle: () => void;
  direction?: "left" | "right"; // default: "left"
  showCheckboxOnDesktop?: boolean; // default: true
  highlightWhenSelected?: boolean; // default: true
  color?: string; // selection highlight color
  selectionStyle?: "strong_border" | "inward_shadow"; // default: "strong_border"
  humanReadable?: string; // accessibility label
  actionDescription?: string; // Optional, short message for user guidance on what the swipe action does
};

type ActionConfig = {
  onSwipe: () => void | Promise<void>;
  icon: GenericIconType;
  color?: string; // default: "moveon.primary"
  humanReadable?: string; // accessibility description
  actionDescription: string; // Mandatory, message for user guidance on what the swipe action does
};

type VisualConfig = {
  showIcons?: boolean; // default: true
  disabled?: boolean; // default: false
  uncoveredBackgroundColor?: string; // background color when swiped (default: moveon.bg). Change it when calling on non-default bg colors (i.e. surface).
};

export type SwipeDirectionForStatefullAction = "left" | "right";

export type SelectionStyle = "strong_border" | "inward_shadow";

export interface WithSwipeSelectorProps {
  children: ReactNode;
  isMobile: boolean; // Required context

  // Nested configurations
  selection?: SelectionConfig;
  action?: ActionConfig;
  visual?: VisualConfig;

  // Legacy support (deprecated)
  onClick?: () => void;
}

function WithSwipeSelector({
  children,
  isMobile,
  selection,
  action,
  visual,
  ...rest
}: WithSwipeSelectorProps) {
  // Extract and set defaults
  const disabled = visual?.disabled ?? false;
  const showIcons = visual?.showIcons ?? true;

  const uncoveredBackgroundColor =
    visual?.uncoveredBackgroundColor ?? "moveon.bg"; // default bg color when swiped to ensure that icons are hidden unless swiped

  // const selectActionDescription = selection?.actionDescription ?? "Select";  // Default value for selection action description
  // differentiate between Select and Unselect for better user guidance
  const selectActionDescription =
    selection?.actionDescription ??
    (selection?.isSelected ? "Unselect" : "Select");
  const isSelected = selection?.isSelected ?? false;
  const checkboxIsSelected = selection?.checkboxIsSelected ?? isSelected;
  const selectDirection = selection?.direction ?? "right";
  const highlightWhenSelected = selection?.highlightWhenSelected ?? true;
  const showCheckboxOnDesktop = selection?.showCheckboxOnDesktop ?? true;
  const selectionColor = selection?.color ?? SELECTED_BG_COLOR;
  const selectionStyle = selection?.selectionStyle ?? "inward_shadow"; // DEFAULT CHANGE

  // Determine swipe colors and icons based on direction
  const swipeLeftColor =
    selectDirection === "left"
      ? (selection?.color ?? "moveon.secondary")
      : (action?.color ?? "moveon.primary");
  const swipeRightColor =
    selectDirection === "left"
      ? (action?.color ?? "moveon.primary")
      : (selection?.color ?? "moveon.secondary");

  const swipeLeftIcon: GenericIconType =
    selectDirection === "left" ? "select" : (action?.icon ?? "to_right");
  const swipeRightIcon: GenericIconType =
    selectDirection === "left" ? (action?.icon ?? "to_right") : "select";

  // 🎨 Chakra color resolution
  const [leftColorResolved, rightColorResolved] = useToken("colors", [
    swipeLeftColor,
    swipeRightColor,
  ]);

  // Inward shadow styles for light and dark modes
  // Light mode: #7e22ce = rgb(126, 34, 206) - moveon.secondary
  // Dark mode: #a855f7 = rgb(168, 85, 247) - moveon.secondary (dark variant)
  // Using higher opacity for better visibility (0.35 main shadow, 0.25 border)
  const inwardShadowStyle = `
    inset 0 2px 8px 0 rgba(126, 34, 206, 0.35), 
    inset 0 0 0 1px rgba(126, 34, 206, 0.25)
  `;
  const inwardShadowStyleDark = `
    inset 0 2px 8px 0 rgba(168, 85, 247, 0.60), 
    inset 0 0 0 1px rgba(168, 85, 247, 0.45)
  `;

  const controls = useAnimation();
  const isMountedRef = useRef(false);
  const x = useMotionValue(0);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // 🧠 MANUAL CLAMP (no constraints)
  const clampedX = useTransform(x, (v) =>
    Math.max(-SWIPE_MAX_DISTANCE, Math.min(SWIPE_MAX_DISTANCE, v)),
  );

  // 🩵 transient background tint - removed as icons are now behind

  const leftActionOpacity = useTransform(
    clampedX,
    [0, SWIPE_REVEAL_START_DISTANCE, SWIPE_TRIGGER_DISTANCE],
    [0, 0, 1],
  );
  const rightActionOpacity = useTransform(
    clampedX,
    [-SWIPE_TRIGGER_DISTANCE, -SWIPE_REVEAL_START_DISTANCE, 0],
    [1, 0, 0],
  );

  // 👁 action description text opacity
  const leftDescriptionOpacity = useTransform(
    clampedX,
    [0, SWIPE_REVEAL_START_DISTANCE, SWIPE_TRIGGER_DISTANCE],
    [0, 0, 1],
  );
  const rightDescriptionOpacity = useTransform(
    clampedX,
    [-SWIPE_TRIGGER_DISTANCE, -SWIPE_REVEAL_START_DISTANCE, 0],
    [1, 0, 0],
  );

  const snapBackToRest = useCallback(() => {
    if (!isMountedRef.current) {
      x.set(0);
      return;
    }

    void controls.start({
      x: 0,
      transition: {
        type: "spring",
        stiffness: RETURN_STIFFNESS,
        damping: RETURN_DAMPING,
      },
    });
  }, [controls, x]);

  // ⚡ INSTANT SWIPE END HANDLER
  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const offsetX = info.offset.x;
      const abs = Math.abs(offsetX);

      // Not enough → return fast
      if (abs < SWIPE_TRIGGER_DISTANCE) {
        snapBackToRest();
        return;
      }

      // FIRE IMMEDIATELY — NO CONFIRMATION
      if (offsetX > 0) {
        // Swipe right
        if (selectDirection === "right") {
          selection?.onToggle();
        } else {
          action?.onSwipe();
        }
      } else {
        // Swipe left
        if (selectDirection === "left") {
          selection?.onToggle();
        } else {
          action?.onSwipe();
        }
      }

      // SNAP BACK instantly
      snapBackToRest();
    },
    [selection, action, selectDirection, snapBackToRest],
  );

  // 🔧 motion props only for mobile
  const motionProps = useMemo(() => {
    if (isMobile) {
      return {
        drag: "x" as const,
        dragMomentum: false, // critical for instant UX
        onDragEnd: handleDragEnd,
      };
    }
    return {};
  }, [isMobile, handleDragEnd]);

  if (disabled) {
    return (
      <Box width="full" {...rest}>
        {children}
      </Box>
    );
  }

  return (
    <Box position="relative" overflow="hidden" width="100%" {...rest}>
      {/* 👁 Directional Icons Layer - positioned behind content */}
      {showIcons && (
        <>
          {/* left icon - positioned at left edge */}
          <motion.div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              left: "0.75rem",
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: `${SWIPE_MAX_DISTANCE}px`,
              zIndex: 1,
              opacity: leftActionOpacity,
              pointerEvents: "none",
            }}
          >
            {action?.actionDescription && (
              <GenericIcon
                icon={swipeRightIcon} // inverted, left swipe shows right proper action icon but on the right side
                color={rightColorResolved} // colors follow direction inversion
                size={iconSizePresets.swipeAction}
              />
            )}
          </motion.div>

          {/* left action description - appears when swiping right */}
          {((selectDirection === "left" && action?.actionDescription) ||
            (selectDirection === "right" && selectActionDescription)) && (
            <motion.div
              style={{
                position: "absolute",
                top: "50%",
                left: `${SWIPE_MAX_DISTANCE + 8}px`,
                transform: "translateY(-50%)",
                opacity: leftDescriptionOpacity,
                pointerEvents: "none",
                zIndex: "1",
                fontSize: "sm",
                fontWeight: "medium",
                color: rightColorResolved,
                whiteSpace: "nowrap",
              }}
            >
              {selectDirection === "left"
                ? action?.actionDescription
                : selectActionDescription}
            </motion.div>
          )}

          {/* right icon - positioned at right edge */}
          <motion.div
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              right: "0.75rem",
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: `${SWIPE_MAX_DISTANCE}px`,
              zIndex: 1,
              opacity: rightActionOpacity,
              pointerEvents: "none",
            }}
          >
            <GenericIcon
              icon={swipeLeftIcon} // inverted, right swipe shows left proper action icon but on the left side
              color={leftColorResolved} // colors follow direction inversion
              size={iconSizePresets.swipeAction}
            />
          </motion.div>

          {/* right action description - appears when swiping left */}
          {((selectDirection === "left" && selectActionDescription) ||
            (selectDirection === "right" && action?.actionDescription)) && (
            <motion.div
              style={{
                position: "absolute",
                top: "50%",
                right: `${SWIPE_MAX_DISTANCE + 8}px`,
                transform: "translateY(-50%)",
                opacity: rightDescriptionOpacity,
                pointerEvents: "none",
                zIndex: "1",
                fontSize: "sm",
                fontWeight: "medium",
                color: leftColorResolved,
                whiteSpace: "nowrap",
                textAlign: "right",
              }}
            >
              {selectDirection === "left"
                ? selectActionDescription
                : action?.actionDescription}
            </motion.div>
          )}
        </>
      )}

      {/* Swipeable Content */}
      <MotionBox
        style={{
          x: clampedX,
          cursor: isMobile ? "grab" : "pointer",
          width: "100%",
          position: "relative",
          zIndex: "2",
        }}
        bg={uncoveredBackgroundColor}
        borderRadius="md"
        animate={controls}
        {...motionProps}
      >
        {/* 👁 icon fade - removed as icons are now behind */}

        {/* Content */}
        {isMobile ? (
          <Box
            bg={uncoveredBackgroundColor}
            borderRadius="md"
            border={
              isSelected &&
              highlightWhenSelected &&
              selectionStyle === "strong_border"
                ? "md"
                : undefined
            }
            borderColor={
              isSelected &&
              highlightWhenSelected &&
              selectionStyle === "strong_border"
                ? selectionColor
                : undefined
            }
            boxShadow={
              isSelected &&
              highlightWhenSelected &&
              selectionStyle === "inward_shadow"
                ? {
                    base: inwardShadowStyle,
                    _dark: inwardShadowStyleDark,
                  }
                : undefined
            }
          >
            {children}
          </Box>
        ) : (
          <HStack justify="space-between" align="center">
            {selection && showCheckboxOnDesktop && (
              <Checkbox.Root
                variant="subtle"
                checked={checkboxIsSelected}
                onCheckedChange={selection.onToggle}
                title={selection.humanReadable}
                aria-label={selection.humanReadable}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
              </Checkbox.Root>
            )}

            <Box
              flex="1"
              bg={uncoveredBackgroundColor} // apply to the while child area for visibility when checkbox is present
              borderRadius="md"
              border={
                isSelected &&
                highlightWhenSelected &&
                selectionStyle === "strong_border"
                  ? "md"
                  : undefined
              }
              borderColor={
                isSelected &&
                highlightWhenSelected &&
                selectionStyle === "strong_border"
                  ? selectionColor
                  : undefined
              }
              boxShadow={
                isSelected &&
                highlightWhenSelected &&
                selectionStyle === "inward_shadow"
                  ? {
                      base: inwardShadowStyle,
                      _dark: inwardShadowStyleDark,
                    }
                  : undefined
              }
            >
              {children}
            </Box>
          </HStack>
        )}
      </MotionBox>
    </Box>
  );
}

export default memo(WithSwipeSelector, (prev, next) => {
  return (
    prev.isMobile === next.isMobile &&
    prev.selection?.isSelected === next.selection?.isSelected &&
    prev.selection?.checkboxIsSelected === next.selection?.checkboxIsSelected &&
    prev.selection?.direction === next.selection?.direction &&
    prev.selection?.highlightWhenSelected ===
      next.selection?.highlightWhenSelected &&
    prev.selection?.onToggle === next.selection?.onToggle &&
    prev.selection?.color === next.selection?.color &&
    prev.selection?.selectionStyle === next.selection?.selectionStyle &&
    prev.selection?.showCheckboxOnDesktop ===
      next.selection?.showCheckboxOnDesktop &&
    prev.action?.onSwipe === next.action?.onSwipe &&
    prev.action?.icon === next.action?.icon &&
    prev.action?.color === next.action?.color &&
    prev.visual?.showIcons === next.visual?.showIcons &&
    prev.visual?.disabled === next.visual?.disabled
  );
});
